import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTopic } from '../hooks/useTopics';
import { useWords } from '../hooks/useWords';
import { useQuizzes } from '../hooks/useQuizzes';
import { WordTable } from '../components/words/WordTable';
import { WordUploader } from '../components/words/WordUploader';
import { ArrowLeft, BookOpen, FileQuestion, Loader2, Pencil, Plus, QrCode } from 'lucide-react';
import { ErrorMessage } from '../components/shared/ErrorMessage';

export default function TopicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const topicId = Number(id);
  const { topic, loading: topicLoading, error: topicError } = useTopic(topicId);
  const { words, loading: wordsLoading, error: wordsError, fetchWords } = useWords(topicId);
  const { quizzes, error: quizzesError } = useQuizzes(topicId);
  const [activeTab, setActiveTab] = useState<'words' | 'quizzes'>('words');

  const invalidId = !Number.isInteger(topicId) || topicId <= 0;

  if (invalidId) {
    return (
      <div className="flex h-64 flex-col items-center justify-center">
        <p className="text-gray-500">Тема не найдена</p>
        <Link to="/topics" className="mt-2 text-sm text-brand-600 hover:underline">
          ← Назад к темам
        </Link>
      </div>
    );
  }

  if (topicLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="flex h-64 flex-col items-center justify-center">
        <p className="text-gray-500">
          {topicError || 'Тема не найдена'}
        </p>
        <Link to="/topics" className="mt-2 text-sm text-brand-600 hover:underline">
          ← Назад к темам
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/topics"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
        >
          <ArrowLeft size={16} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{topic.title}</h1>
          {topic.description && <p className="text-sm text-gray-500">{topic.description}</p>}
        </div>
      </div>

      <div className="flex gap-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('words')}
          className={`flex items-center gap-2 border-b-2 px-1 pb-2 text-sm font-medium ${
            activeTab === 'words'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <BookOpen size={16} />
          Слова ({topic.wordCount})
        </button>
        <button
          onClick={() => setActiveTab('quizzes')}
          className={`flex items-center gap-2 border-b-2 px-1 pb-2 text-sm font-medium ${
            activeTab === 'quizzes'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileQuestion size={16} />
          Тесты ({topic.quizCount})
        </button>
      </div>

      {activeTab === 'words' && (
        <div className="space-y-4">
          <WordUploader topicId={topicId} onSuccess={fetchWords} />
          {wordsError && <ErrorMessage message={wordsError} />}
          {wordsLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
            </div>
          ) : (
            <WordTable words={words} topicId={topicId} onChange={fetchWords} />
          )}
        </div>
      )}

      {activeTab === 'quizzes' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => navigate(`/topics/${topicId}/quizzes/new`)}
              className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              <Plus size={16} />
              Создать тест
            </button>
          </div>
          {quizzesError && <ErrorMessage message={quizzesError} />}
          {quizzes.length === 0 ? (
            <div className="flex h-32 flex-col items-center justify-center rounded-xl border border-dashed border-gray-300">
              <FileQuestion size={24} className="text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">Пока нет тестов для этой темы</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {quizzes.map((quiz) => (
                <div
                  key={quiz.id}
                  className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{quiz.title}</h3>
                      <p className="mt-1 text-xs text-gray-500 capitalize">{quiz.quizType.replace(/-/g, ' ')}</p>
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        quiz.isPublished
                          ? 'bg-green-50 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {quiz.isPublished ? 'Опубликован' : 'Черновик'}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate(`/quizzes/${quiz.id}/publish`)}
                      className="flex items-center gap-1 rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-100"
                    >
                      <QrCode size={14} />
                      Публикация
                    </button>
                    {/* The /edit route existed but nothing linked to it, so editing a quiz
                        was unreachable. QuizBuilderPage now hydrates it correctly. */}
                    <button
                      onClick={() => navigate(`/quizzes/${quiz.id}/edit`)}
                      className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <Pencil size={14} />
                      Изменить
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
